<template>
  <hamv-dialog :dialogProps="dialogProps" :showModal="showModal">
    <span class="el-dialog__title" data-qa="Dialog-EditNotificationAdd-Title" slot="header">{{ $t('notificationsEditTitleEdit') }}</span>
    <el-form
      :model="data"
      @submit.native.prevent="editNotification"
      id="editForm"
      label-position="left"
      ref="data"
      v-loading="gettingNotification"
    >
      <el-form-item
        :error="errors.has('familyName') ? errors.first('familyName') : ''"
        :label="$t('notificationsEditInputFamilyNameLabel')"
        :label-width="labelWidth"
        :show-message="errors.has('familyName')"
        data-qa="Dialog-EditNotificationFamilyName-Item"
      >
        <el-select
          :placeholder="$t('notificationsEditInputFamilyNamePlaceholder')"
          :data-vv-as="$t('notificationsEditInputFamilyNamePlaceholder')"
          class="full-width"
          data-qa="Dialog-EditNotificationFamilyName-Select"
          multiple
          name="familyName"
          required
          v-model="data.familyNames"
          v-validate="{ rules: { required: true } }"
        >
          <el-option
            v-for="name in familyNames"
            :key="name"
            :label="name"
            :value="name"
          >
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item
        :label-width="labelWidth"
        class="column-describe"
        data-qa="Dialog-EditNotificationFamilyName-Description"
      >
        <div class="fit-line-height">
          <i>{{ $t('notificationsEditInputFamilyNameHint') }}</i>
        </div>
      </el-form-item>
      <el-form-item
        :error="errors.has('ruleType') ? errors.first('ruleType') : ''"
        :label="$t('notificationsEditInputRuleTypeLabel')"
        :label-width="labelWidth"
        :show-message="errors.has('ruleType')"
        data-qa="Dialog-EditNotificationRuleType-Item"
      >
        <el-select
          :placeholder="$t('notificationsEditInputRuleTypePlaceholder')"
          class="full-width"
          data-qa="Dialog-EditNotificationRuleType-Select"
          name="ruleType"
          required
          v-model="data.ruleType"
          v-validate="{ rules: { required: true } }"
        >
          <el-option
            :key="type.key"
            :label="type.label"
            :value="type.value"
            v-for="type in ruleTypes"
          >
          </el-option>
        </el-select>
        <el-row :gutter="5" class="status-rule-block" v-if="isStatusRule">
          <el-col :span="8">
            <el-form-item :error="errors.has('key') ? errors.first('key') : ''" :show-message="false">
              <el-input
                :disabled="true"
                :placeholder="$t('notificationsEditInputKeyPlaceholder')"
                clearable
                data-qa="Dialog-EditNotificationRuleTypeConditionsKey-Input"
                name="key"
                required
                type="text"
                v-model="data.conditions.key"
                v-validate="{ rules: { required: true, regex: validate.deviceKey } }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :error="errors.has('operator') ? errors.first('operator') : ''" :show-message="false">
              <el-select
                :placeholder="$t('notificationsEditInputOperatorPlaceholder')"
                data-qa="Dialog-EditNotificationRuleTypeConditionsOperators-Select"
                name="operator"
                required
                v-model="data.conditions.op"
                v-validate="{ rules: { required: true } }"
              >
                <el-option
                  :key="op.key"
                  :label="op.label"
                  :value="op.value"
                  v-for="op in operators"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :error="errors.has('target') ? errors.first('target') : ''" :show-message="false">
              <el-input
                :placeholder="$t('notificationsEditInputTargetPlaceholder')"
                :data-vv-as="$t('notificationsEditInputTargetPlaceholder')"
                clearable
                data-qa="Dialog-EditNotificationRuleTypeConditionsTarget-Input"
                name="target"
                required
                type="text"
                v-model="data.conditions.target"
                v-validate="{ rules: { required: true, numeric: true } }"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="help is-danger conditions-error" v-if="hasConditionsError">
          <p v-if="errors.has('key')">{{ $t('notificationsEditInputKeyError') }}</p>
          <p v-if="errors.has('operator')">{{ errors.first('operator') }}</p>
          <p v-if="errors.has('target')">{{ errors.first('target') }}</p>
        </div>
      </el-form-item>
      <el-form-item
        :label-width="labelWidth"
        class="column-describe"
        data-qa="Dialog-EditNotificationRuleType-Description"
      >
        <div class="fit-line-height">
          <i>{{ $t('notificationsEditInputRuleTypeHint') }}</i>
        </div>
      </el-form-item>
      <el-form-item
        :error="errors.has('title') ? errors.first('title') : ''"
        :label="$t('notificationsEditInputTitleLabel')"
        :label-width="labelWidth"
        :show-message="errors.has('title')"
        data-qa="Dialog-EditNotificationTitle-Item"
      >
        <el-input
          :placeholder="$t('notificationsEditInputTitlePlaceholder')"
          :data-vv-as="$t('notificationsEditInputTitlePlaceholder')"
          clearable
          data-qa="Dialog-EditNotificationTitle-Input"
          name="title"
          required
          type="text"
          v-model="data.title"
          v-validate="{ rules: { required: true, max: 40 } }"
        />
      </el-form-item>
      <el-form-item
        :label-width="labelWidth"
        class="column-describe"
        data-qa="Dialog-EditNotificationTitle-Description"
      >
        <div class="fit-line-height">
          <i>{{ $t('notificationsEditInputTitleHint1') }}</i><br />
          <i>{{ $t('notificationsEditInputTitleHint2') }}</i>
        </div>
      </el-form-item>
      <el-form-item
        :error="errors.has('message') ? errors.first('message') : ''"
        :label="$t('notificationsEditInputMessageLabel')"
        :label-width="labelWidth"
        :show-message="errors.has('message')"
        data-qa="Dialog-EditNotificationMessage-Item"
      >
        <el-input
          :placeholder="$t('notificationsEditInputMessagePlaceholder')"
          :data-vv-as="$t('notificationsEditInputMessageLabel')"
          clearable
          data-qa="Dialog-EditNotificationMessage-Input"
          name="message"
          required
          type="text"
          v-model="data.message"
          v-validate="{ rules: { required: true, max: 160 } }"
        />
      </el-form-item>
      <el-form-item
        :label-width="labelWidth"
        class="column-describe"
        data-qa="Dialog-EditNotificationMessage-Description"
      >
        <div class="fit-line-height">
          <i>{{ $t('notificationsEditInputMessageHint1') }}</i><br />
          <i>{{ $t('notificationsEditInputMessageHint2') }}</i>
        </div>
      </el-form-item>
    </el-form>
    <span slot="actions">
      <el-button
        @click="closePopup"
        class="btn-close"
        data-qa="Dialog-EditNotification-Button-Close"
        type="text"
      >
        {{ $t('notificationsEditDialogButtonCloseText') }}
      </el-button>
      <el-button
        @click="editNotification"
        :disabled="loading || errors.any()"
        :loading="loading"
        data-qa="Dialog-EditNotification-Button-Save"
        type="warning"
      >
        {{ $t('notificationsEditDialogButtonSaveText') }}
      </el-button>
    </span>
  </hamv-dialog>
</template>

<script>
  import main from './NotificationsEdit.js'
  export default main
</script>

<style lang="scss" scoped>
  @import './NotificationsEdit.scss'
</style>



// WEBPACK FOOTER //
// NotificationsEdit.vue?5ab86124